import { OrderDetailsSkeletonComponent } from '@dss-libs/portal/ui/portal-common-components';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OrdersService } from '@dss-libs/portal/data-access/contract-order';
import { EventTypeEnum, OrderStatus, TConfirmationModalData } from '@dss-libs/shared/type/common-types';
import { SnackbarService, ModalUiService } from '@dss-libs/shared/utility/common-services';
import { TranslocoDirective } from '@jsverse/transloco';
import { TranslocoDatePipe, TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { finalize } from 'rxjs';
import {
  ButtonComponent,
  ChipComponent,
  ConfirmationModalComponent,
  ProgressBarComponent,
} from '@dss-libs/shared/ui/common-components';
import { MatTooltip } from '@angular/material/tooltip';
import {
  OrderParticipantTab,
  OrderStatusTranslationMapper,
  ActionStatus,
  RecipientType,
  TabDefinition,
} from '@dss-libs/portal/type/portal-types';
import { getOrderStatusBadgeClass } from '../../../mappers/order-status.mapper';
import { ActivityLogCardComponent } from './activity-log-card/activity-log-card.component';
import { AttachmentsCardComponent } from './attachments-card/attachments-card.component';
import { RecipientsPanelComponent } from './recipients-panel/recipients-panel.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { TooltipIfTruncatedDirective } from '@dss-libs/shared/ui/common-directives';

@Component({
  selector: 'dss-libs-feature-order-details',
  imports: [
    TranslocoDirective,
    TranslocoDatePipe,
    TranslocoDecimalPipe,
    ButtonComponent,
    ProgressBarComponent,
    OrderDetailsSkeletonComponent,
    MatTooltip,
    ActivityLogCardComponent,
    AttachmentsCardComponent,
    RecipientsPanelComponent,
    MatIcon,
    TooltipIfTruncatedDirective,
    ChipComponent,
  ],
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslocoDatePipe],
})
export class OrderDetailsComponent {
  readonly #router = inject(Router);
  readonly #contractOrderService = inject(OrdersService);
  readonly #snackbarService = inject(SnackbarService);
  readonly #modalService = inject(ModalUiService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly OrderStatusTranslationMapper = OrderStatusTranslationMapper;
  protected readonly getOrderStatusBadgeClass = getOrderStatusBadgeClass;

  readonly orderId = input.required<string>();
  readonly orderDetailsResource = this.#contractOrderService.getOrderDetailsResource(this.orderId);
  readonly orderActivityResource = this.#contractOrderService.getOrderActivitiesResource(this.orderId);

  readonly order = computed(() =>
    this.orderDetailsResource.hasValue() ? this.orderDetailsResource.value() : undefined
  );
  readonly activities = computed(() =>
    this.orderActivityResource.hasValue() ? this.orderActivityResource.value() : undefined
  );
  readonly isLoading = computed(() => this.orderActivityResource.isLoading() || this.orderDetailsResource.isLoading());
  readonly hasError = computed(() => !!this.orderDetailsResource.error() && !this.order());

  readonly signers = computed(
    () => this.order()?.signers?.filter((s) => s.recipientType === RecipientType.Signer) ?? []
  );
  readonly reviewers = computed(
    () => this.order()?.signers?.filter((s) => s.recipientType === RecipientType.Reviewer) ?? []
  );
  readonly receivers = computed(() => this.order()?.notificationRecipients ?? []);

  readonly signedCount = computed(() => this.signers().filter((s) => s.actionStatus === ActionStatus.Completed).length);
  readonly totalSigners = computed(() => this.signers().length);
  readonly reviewedCount = computed(
    () => this.reviewers().filter((r) => r.actionStatus === ActionStatus.Completed).length
  );
  readonly hasReviewers = computed(() => this.reviewers().length > 0);
  readonly totalParticipants = computed(() => this.totalSigners() + this.reviewers().length);
  readonly completedParticipants = computed(() => this.signedCount() + this.reviewedCount());
  readonly completionRatio = computed(() =>
    this.totalParticipants() > 0 ? this.completedParticipants() / this.totalParticipants() : 0
  );
  readonly completionPercent = computed(() => Math.round(this.completionRatio() * 100));
  readonly isOrderRejected = computed(() => this.order()?.status === OrderStatus.Declined);

  readonly daysRemaining = computed(() => {
    const expiryDate = this.order()?.expiryDate;
    if (!expiryDate) return null;
    const diff = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  });

  readonly isExpiringSoon = computed(() => {
    const days = this.daysRemaining();
    return days !== null && days <= 7;
  });

  readonly tabs = computed<TabDefinition[]>(() =>
    [
      { key: OrderParticipantTab.Signers, labelKey: 'ORDER-DETAILS.SIGNERS', count: this.signers().length },
      { key: OrderParticipantTab.Reviewers, labelKey: 'ORDER-DETAILS.REVIEWERS', count: this.reviewers().length },
      { key: OrderParticipantTab.Receivers, labelKey: 'ORDER-DETAILS.RECEIVERS', count: this.receivers().length },
    ].filter((tab) => tab.count > 0)
  );

  readonly sendingReminderForId = signal<string | null>(null);

  readonly canSendReminder = computed(() => {
    const status = this.order()?.status;
    if (!status) return false;
    return ![OrderStatus.Declined, OrderStatus.Expired, OrderStatus.Cancelled].includes(status);
  });

  readonly canCancelOrder = computed(() => {
    const status = this.order()?.status;
    if (!status) return false;
    return ![OrderStatus.Completed, OrderStatus.Cancelled, OrderStatus.Declined, OrderStatus.Expired].includes(status);
  });

  readonly isCancellingOrder = signal(false);

  goBack(): void {
    void this.#router.navigate(['/orders']);
  }

  openCancelOrderModal(): void {
    const data: TConfirmationModalData = {
      svgIcon: 'danger-triangle-outline',
      iconVariant: 'danger',
      title: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.TITLE',
      subtitle: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.SUBTITLE',
      message: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.DESCRIPTION',
      callout: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.CALLOUT',
      primaryButtonLabel: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.CANCEL-ORDER',
      primaryButtonColor: 'danger',
      secondaryButtonLabel: 'ORDER-DETAILS.CANCEL-ORDER-MODAL.KEEP-ORDER',
    };
    this.#modalService.openAbstractModal({
      component: ConfirmationModalComponent,
      dialogData: data,
      modalDimensionsConfig: { width: '30rem' },
      onSubmit: () => this.#confirmCancelOrder(),
    });
  }

  #confirmCancelOrder(): void {
    this.isCancellingOrder.set(true);
    this.#contractOrderService
      .cancelOrder(this.orderId())
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => this.isCancellingOrder.set(false))
      )
      .subscribe({
        next: () => {
          this.orderDetailsResource.reload();
          this.#snackbarService.showToaster(EventTypeEnum.SUCCESS, 'ORDER-DETAILS.ORDER-CANCELLED.SUCCESS');
        },
        error: () => this.#snackbarService.showToaster(EventTypeEnum.ERROR, 'ORDER-DETAILS.ORDER-CANCELLED.ERROR'),
      });
  }

  sendReminder(signerId: string): void {
    this.sendingReminderForId.set(signerId);
    this.#contractOrderService
      .sendReminders(this.orderId(), [signerId])
      .pipe(finalize(() => this.sendingReminderForId.set(null)))
      .subscribe({
        next: () => {
          this.orderDetailsResource.reload();
          this.orderActivityResource.reload();
          this.#snackbarService.showToaster(EventTypeEnum.SUCCESS, 'CONTRACT_ORDER.REMINDERS_SENT_SUCCESSFULLY');
        },
        error: () =>
          this.#snackbarService.showToaster(EventTypeEnum.ERROR, 'CONTRACT_ORDER.ORDER_WAS_CANCELLED_OR_DECLINED'),
      });
  }
}
